import React, { useCallback, useMemo } from 'react';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import CollapsibleCard from '../../../../components/CollapsibleCard';
import StackViewEntry from '../views/StackViewEntry';
import TableViewEntry from '../views/TableViewEntry';
import { useDispatch, useSelector } from 'react-redux';
import { setMappedColumns, setErrors, setActiveTab, setAdditionalColumns } from '../../../../store/uploads/actions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip';
import constants from '../../../../commonUtils/constants';
import { extractValues } from '../../../../commonUtils/string.helper';
import MismatchInfo from './MismatchInfo';
import IconButton from '../../../../components/IconButton';
import { ChevronDoubleRightIcon } from '@heroicons/react/24/outline';

const MappedFields = ({ hideFieldSelection = false, hideNavigationButtons = false }) => {
    const dispatch = useDispatch();
    const { mappedColumns, activeTab, destinationColumns, destinationDataTypes } = useSelector((state) => state.Uploads);

    // Create a single columns array that will be shared by all SelectListInput components
    const baseColumns = useMemo(() => {
        return Object.keys(destinationColumns).map((item) => {
            const label = `${item} (${destinationColumns[item]})`;
            return { 
                value: label, 
                label,
                columnName: item // Store the column name for easier lookup
            };
        });
    }, [destinationColumns]);

    // Create a map of taken columns for O(1) lookup
    const takenColumnsMap = useMemo(() => {
        const taken = new Map();
        mappedColumns.forEach((col, index) => {
            if (col.Mapped_Column && col.Mapped_DataType) {
                const key = `${col.Mapped_Column} (${col.Mapped_DataType})`;
                taken.set(key, index); // Store the index that's using this column
            }
        });
        return taken;
    }, [mappedColumns]);

    // Optimized change handler with better state management
    const handleChangeMappedField = useCallback((index, newValue) => {
        const updatedMappedColumns = [...mappedColumns];

        if (newValue === "0") {
            // Clear the mapping
            updatedMappedColumns[index] = {
                ...updatedMappedColumns[index],
                "Mapped_Column": "",
                "Mapped_DataType": ""
            };
        } else {
            const { destName } = extractValues(newValue);

            if (index !== undefined && destName) {
                // Find if this destination is already mapped by another field
                const existingMappingIndex = updatedMappedColumns.findIndex(
                    (col, idx) => idx !== index && col["Mapped_Column"] === destName
                );

                // Clear the existing mapping if found
                if (existingMappingIndex !== -1) {
                    updatedMappedColumns[existingMappingIndex] = {
                        ...updatedMappedColumns[existingMappingIndex],
                        "Mapped_Column": "",
                        "Mapped_DataType": ""
                    };
                }

                // Set the new mapping
                updatedMappedColumns[index] = {
                    ...updatedMappedColumns[index],
                    "Mapped_Column": destName,
                    "Mapped_DataType": destinationColumns[destName]
                };
            }
        }

        // Batch the updates to prevent multiple re-renders
        dispatch(setMappedColumns(updatedMappedColumns));
        dispatch(setErrors(constants.upload.dataInitialState));
    }, [dispatch, mappedColumns, destinationColumns]);

    const moveToAdditionalFields = useCallback(() => {
        dispatch(setActiveTab("additional_fields"));
        const updatedAdditionalFields = mappedColumns.reduce((acc, col) => {
            if (["", constants.upload.matchNotFound].includes(col["Mapped_Column"])) {
                acc.push({
                    ...col,
                    selected: false,
                    "Mapped_Column": col['Mapped_Column'] === constants.upload.matchNotFound ? "" : col['Mapped_Column'],
                    "Mapped_DataType": col['Mapped_Column'] === constants.upload.matchNotFound ? "" : col['Mapped_DataType']
                });
            }
            return acc;
        }, []);
        dispatch(setAdditionalColumns(updatedAdditionalFields));
    }, [dispatch, mappedColumns]);

    // Memoize individual row components to prevent unnecessary re-renders
    const MemoizedTableEntry = React.memo(({ item, index, currentValue }) => {
        // Generate columns specific to this entry (with used status calculated per entry)
        const entryColumns = baseColumns.map(col => ({
            ...col,
            used: takenColumnsMap.has(col.value) && takenColumnsMap.get(col.value) !== index
        }));

        const hasDataTypeMismatch = (item['Input_DataType'] !== item['Mapped_DataType']) && 
                                (item['Mapped_DataType'] !== "" && item['Mapped_DataType'] !== "NA");

        const commonProps = {
            index,
            columns: entryColumns,
            dataTypes: destinationDataTypes,
            source: item['Input_Column'],
            sourceType: item['Input_DataType'],
            destination: item['Mapped_Column'],
            destType: item['Mapped_Column'] === constants.upload.matchNotFound ? "" : item['Mapped_DataType'],
            changeMappedField: handleChangeMappedField,
            viewOnly: hideFieldSelection
        };

        return (
            <div className={`py-1 px-1 rounded ${hasDataTypeMismatch ? 'bg-yellow-200' : ''}`}>
                {/* Desktop view */}
                <div className="hidden md:block">
                    <TableViewEntry {...commonProps} />
                </div>
                
                {/* Mobile view */}
                <div className="block md:hidden">
                    <StackViewEntry {...commonProps} />  
                </div>
            </div>
        );
    });

    MemoizedTableEntry.displayName = 'MemoizedTableEntry';

    // Render mapped columns with memoized entries
    const renderMappedColumns = useMemo(() => {
        if (!mappedColumns?.length) return null;

        return mappedColumns.map((item, index) => {
            // Create a stable key that includes the current mapping to ensure re-render when needed
            const currentValue = item['Mapped_Column'] ? `${item['Mapped_Column']} (${item['Mapped_DataType']})` : '';
            
            return (
                <MemoizedTableEntry
                    key={`${index}-${currentValue}`}
                    item={item}
                    index={index}
                    currentValue={currentValue}
                />
            );
        });
    }, [mappedColumns]);

    const NextBtn = () => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="inline">
                        <IconButton
                            size="iconsm"
                            variant="nav_btn"
                            onClick={moveToAdditionalFields}
                            Icon={ChevronDoubleRightIcon}
                            disabled={activeTab === "additional_fields"}
                        />
                    </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                    <p>Move to Additional Fields</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    return (
        <CollapsibleCard
            className="m-0"
            title={"Mapped Fields"} 
            showCollapseButton={true}
            headerButtons={!hideNavigationButtons && <NextBtn/>}
        >
            <ScrollArea style={{ height: `calc(${constants.upload.cardHeight} - 28px)` }}>
                <div className='flex flex-col gap-0 gap-y-2 md:gap-y-1 mr-3'>
                    <div className="hidden md:grid grid-cols-3 gap-2">
                        <p className='col-span-1 pl-1'>Source (File Field)</p>
                        <p className='col-span-2'>Destination (DB Field)</p>
                    </div>
                    {renderMappedColumns}
                </div>
            </ScrollArea>
            <MismatchInfo className='items-start'/>
        </CollapsibleCard>
    );
};

export default MappedFields;